import { useCustomMutation } from '@refinedev/core';
import { useCallback } from 'react';
import type { ExposureStimulus } from '../../schema/be-graphql-generated';
import { hasGraphQLErrorCode } from '../../utils/graphqlErrors';

/**
 * E21 · the PORTAL's exposition runner, on E20's three commands (`startExposition` /
 * `recordExpositionReaction` / `finishExposition`, buildOrder 5–7). The portal twin of the mobile
 * `screens/es/exposure/useExpositionEsCommands.ts` WP4 built, with one difference that is the whole
 * point of E21: every call carries `subject` — the CLIENT the therapist is acting FOR.
 *
 * WHY THE RUNNER CHANGED AT ALL. In the legacy era a run was client state: the drawer built a whole
 * `{createdAt, anticipatedSud, suds[], reactionNotes}` record in a React draft and posted it inside
 * `upsertExposureStimulus.data.expositions` when the therapist pressed Save — so a run that was
 * never saved never happened, and a browser refresh lost it. From E20 WP2 each beat is its own
 * durable fact and the nested legacy array is their PROJECTION. The upside is that a run survives a
 * closed tab; the cost WP5 had to name was that a recorded reaction could not be amended, which is
 * why the runner asks for the level BEFORE the tap instead of appending a point and letting the
 * therapist drag it afterwards.
 *
 * THAT COST IS NOW PAID OFF, and the pre-tap slider stays anyway. The founder ruled on 2026-09-04
 * (`amend-a-recorded-reaction`) that a recorded point CAN be corrected — as a correction event, so
 * the original reading stays a fact and the correction supersedes it for display. `amendReaction`
 * below is that command. Choosing the level first is no longer forced; it is kept because it is
 * the honest gesture for a LIVE run (the number you are recording now), while correcting is a
 * deliberate second act on a reading that came out wrong.
 *
 * EVERY MUTATION ANSWERS WITH THE ROW. `ExpositionPayload {expositionId, exposureStimulus}` carries
 * the legacy-shaped satellite row the inline back-projection wrote in the SAME transaction
 * (read-your-writes, plan 04 D3), so the drawer re-renders SERVER TRUTH after every beat rather
 * than a local guess it hopes matches.
 *
 * ES_MODE_MISMATCH is handled by the caller, not here: it means the era this page loaded with is
 * stale (T112), and only the page knows what to do about it (re-ask the era, then fall back to the
 * legacy drawer). `isEraMismatch` is exported so the caller does not string-match.
 */

/**
 * The fields every exposition mutation returns — enough to re-render the run and the history.
 *
 * `reactionId` and `amendedFrom` ride on every point since the AMEND slice (founder ruling
 * `amend-a-recorded-reaction`, 2026-09-04): the handle is what a correction addresses, and
 * `amendedFrom` is the original reading a corrected point superseded, which is what lets the
 * drawer show the point AS CORRECTED without asking the server a second question.
 */
const EXPOSITION_PAYLOAD_FIELDS = [
  'expositionId',
  'reactionId',
  {
    exposureStimulus: [
      '_id',
      'exposureStimulusId',
      'updatedAt',
      {
        expositions: [
          'expositionId',
          'createdAt',
          'duration',
          'anticipatedSud',
          'reactionNotes',
          'expectedOutcome',
          'expectedLikelihood',
          'outcomeOccurred',
          'likelihoodAfter',
          'learned',
          {
            suds: [
              'sud',
              'reactedAt',
              'reactionId',
              { amendedFrom: ['sud', 'reactedAt'] },
            ],
          },
        ],
      },
    ],
  },
];

/** The optional answers before Start — every key absent unless the person answered it. */
export type ExpositionExpectancy = {
  expectedOutcome?: string | null;
  expectedLikelihood?: number | null;
};

/** The optional answers after Stop — every key absent unless the person answered it. */
export type ExpositionOutcomeAnswers = {
  outcomeOccurred?: 'yes' | 'partly' | 'no' | null;
  likelihoodAfter?: number | null;
  learned?: string | null;
};

export type ExpositionResult = {
  expositionId: string;
  /** The point this beat recorded or corrected — absent on start and finish. */
  reactionId?: string | null;
  exposureStimulus: ExposureStimulus | null;
};

/** Did the server say this subject is not on the ES path after all? (T112 — the era went stale.) */
export const isEraMismatch = (error: unknown): boolean =>
  hasGraphQLErrorCode(error, 'ES_MODE_MISMATCH');

/**
 * EVERY WAY THE SERVER SAYS "THE ERA THIS PAGE LOADED WITH IS STALE" (T112).
 *
 * `ES_MODE_MISMATCH` alone is not the set, and reading it as though it were is what let a mid-run
 * flip swallow a therapist's work. Three codes mean the same thing from three directions:
 *
 *   • `ES_MODE_MISMATCH`  — an ES-native operation, subject not (yet / any longer) on the ES path;
 *   • `ES_RESEND_AS_ES`   — a LEGACY operation, subject already on it: resend as the ES commands;
 *   • `EXPOSITION_EDIT_NOT_PORTED` — E20's narrow field guard (T115 (b)). It fires when a
 *     legacy-shaped save echoes an `expositions[]` that would CHANGE what the log holds, and for a
 *     page that loaded before a flip that is precisely "your era is stale": the drawer built a run
 *     the old way, and the log took ownership of the run history underneath it.
 *
 * The caller decides what to do — re-issue through the ES commands where the model can express the
 * write, or refuse OUT LOUD where it cannot. What it may never do is treat the refusal as nothing.
 */
export const isEraSignal = (error: unknown): boolean =>
  isEraMismatch(error) ||
  hasGraphQLErrorCode(error, 'ES_RESEND_AS_ES') ||
  hasGraphQLErrorCode(error, 'EXPOSITION_EDIT_NOT_PORTED');

/** Did the server say the row has not been converted yet? (T95a — the row decides the era.) */
export const isNotConverted = (error: unknown): boolean =>
  hasGraphQLErrorCode(error, 'EXPOSURE_NOT_CONVERTED');

export function useExpositionEsCommands({
  subject,
  exposureStimulusId,
}: {
  /** The CLIENT the therapist acts for — `subject` on every command (ATTR-1 / D-fhir). */
  subject: string | undefined;
  /** The legacy `exposureStimuli/<key>` handle, which is what all three inputs take. */
  exposureStimulusId: string | undefined;
}) {
  const { mutate: startMutation, isLoading: starting } =
    useCustomMutation<ExpositionResult>();
  const { mutate: reactMutation, isLoading: reacting } =
    useCustomMutation<ExpositionResult>();
  const { mutate: finishMutation, isLoading: finishing } =
    useCustomMutation<ExpositionResult>();
  const { mutate: amendMutation, isLoading: amending } =
    useCustomMutation<ExpositionResult>();

  const send = useCallback(
    (
      mutate: ReturnType<typeof useCustomMutation<ExpositionResult>>['mutate'],
      operation: string,
      inputType: string,
      value: Record<string, unknown>,
    ) =>
      new Promise<ExpositionResult | null>((resolve, reject) => {
        mutate(
          {
            url: '',
            method: 'post',
            values: {},
            meta: {
              operation,
              fields: EXPOSITION_PAYLOAD_FIELDS,
              variables: {
                input: { name: 'input', type: inputType, value },
              },
            },
            // the caller reports the outcome — never the raw GraphQL error toast
            errorNotification: false,
          },
          {
            onSuccess: (res) =>
              resolve((res?.data as ExpositionResult | undefined) ?? null),
            onError: (e) => reject(e),
          },
        );
      }),
    [],
  );

  const startExposition = useCallback(
    (anticipatedSud: number | null, expectancy: ExpositionExpectancy = {}) =>
      send(startMutation, 'startExposition', 'StartExpositionInput!', {
        subject,
        exposureStimulusId,
        anticipatedSud,
        // D-expectancies-2026-10-07 — sent ONLY when answered; "not asked" is absence, never 0
        ...(expectancy.expectedOutcome
          ? { expectedOutcome: expectancy.expectedOutcome }
          : {}),
        ...(expectancy.expectedLikelihood != null
          ? { expectedLikelihood: expectancy.expectedLikelihood }
          : {}),
      }),
    [send, startMutation, subject, exposureStimulusId],
  );

  const recordReaction = useCallback(
    (expositionId: string, sud: number, reactedAt?: string) =>
      send(
        reactMutation,
        'recordExpositionReaction',
        'RecordExpositionReactionInput!',
        {
          subject,
          exposureStimulusId,
          expositionId,
          sud,
          // the rating's WALL-CLOCK is the client's (the runner lets it be corrected); absent = now
          ...(reactedAt ? { reactedAt } : {}),
        },
      ),
    [send, reactMutation, subject, exposureStimulusId],
  );

  const finishExposition = useCallback(
    (
      expositionId: string,
      duration: number | null,
      notes: string | null,
      outcome: ExpositionOutcomeAnswers = {},
    ) =>
      send(finishMutation, 'finishExposition', 'FinishExpositionInput!', {
        subject,
        exposureStimulusId,
        expositionId,
        duration,
        notes,
        // D-expectancies-2026-10-07 — sent ONLY when answered; "not asked" is absence, never 0
        ...(outcome.outcomeOccurred
          ? { outcomeOccurred: outcome.outcomeOccurred }
          : {}),
        ...(outcome.likelihoodAfter != null
          ? { likelihoodAfter: outcome.likelihoodAfter }
          : {}),
        ...(outcome.learned ? { learned: outcome.learned } : {}),
      }),
    [send, finishMutation, subject, exposureStimulusId],
  );

  /**
   * CORRECT one recorded point (founder ruling `amend-a-recorded-reaction`, 2026-09-04). It is a
   * new fact, never an edit: `ExpositionReactionRecorded` stays in the log and the correction
   * supersedes it for display. `reactionId` comes off the point the drawer is showing — the server
   * minted it when the reaction was recorded, or it is the derived handle of a point conversion
   * carried; the portal never invents one.
   *
   * Both `sud` and `reactedAt` travel even when only one of them moved: the command states the
   * whole corrected point, so nothing downstream has to work out which half changed.
   */
  const amendReaction = useCallback(
    (
      expositionId: string,
      reactionId: string,
      sud: number,
      reactedAt: string,
    ) =>
      send(
        amendMutation,
        'amendExpositionReaction',
        'AmendExpositionReactionInput!',
        {
          subject,
          exposureStimulusId,
          expositionId,
          reactionId,
          sud,
          reactedAt,
        },
      ),
    [send, amendMutation, subject, exposureStimulusId],
  );

  return {
    startExposition,
    recordReaction,
    finishExposition,
    amendReaction,
    busy: starting || reacting || finishing || amending,
  };
}
